import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import VmIdDashboardComponent from "./component";

const VmIdDashboardContainer = (props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uidData, setUidData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [createdUids, setCreatedUids] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // QR Code states
  const [showBulkQRDialog, setShowBulkQRDialog] = useState(false);
  const [qrUids, setQrUids] = useState([]);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [mappingStateFilter, setMappingStateFilter] = useState('all');
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const [mappingFilterAnchorEl, setMappingFilterAnchorEl] = useState(null);

  // Dialog states
  const [createUidOpen, setCreateUidOpen] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [selectedUids, setSelectedUids] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showListPage, setShowListPage] = useState(false);

  // Form data states
  const [newUidData, setNewUidData] = useState({ uid: '' });

  const pageSize = 50;

  // Load initial data on component mount
  useEffect(() => {
    loadUids(0);
  }, []);

  // Function to load UIDs with page number
  const loadUids = (pageNumber = 0, searchValue = searchTerm) => {
    setLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/mappings/table?pageSize=${pageSize}&pageNumber=${pageNumber}&search=${encodeURIComponent(searchValue)}`,
      "GET",
      undefined,
      (response) => {
        if (response && Array.isArray(response)) {
          const uids = response || [];
          
          if (pageNumber === 0) {
            setUidData(uids);
          } else {
            setUidData(prevData => [...prevData, ...uids]);
          }
          
          setHasMore(uids.length === pageSize);
          setTotalCount(uids.length);
        } else {
          setError("Failed to fetch UID data");
        }
        setLoading(false);
        setCurrentPage(pageNumber);
      },
      (error) => {
        setError("Failed to fetch UID data");
        setLoading(false);
      }
    );
  };

  // Load next page of data
  const loadNextPage = () => {
    loadUids(currentPage + 1);
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
    setSelectedUids(new Set());
    setSelectAll(false);
    loadUids(0, value);
  };

  // Handle individual checkbox selection
  const handleUidSelect = (uidId) => {
    const newSelected = new Set(selectedUids);
    if (newSelected.has(uidId)) {
      newSelected.delete(uidId);
    } else {
      newSelected.add(uidId);
    }
    setSelectedUids(newSelected);
    
    // Update selectAll based on current page selection
    const currentPageUids = uidData.map(uid => uid.uid);
    const allCurrentSelected = currentPageUids.every(id => newSelected.has(id));
    setSelectAll(allCurrentSelected);
  };

  // Handle select all checkbox - UPDATED: Select only filtered UIDs
  const handleSelectAll = () => {
    // Apply filters locally to get filtered UIDs
    const filteredUids = uidData.filter(uid => {
      const statusMatch = statusFilter === 'all' || 
        (uid.status?.toString().toLowerCase() === statusFilter.toLowerCase());
      
      const mappingStateMatch = mappingStateFilter === 'all' || 
        (uid.mappingState?.toLowerCase() === mappingStateFilter.toLowerCase());
      
      return statusMatch && mappingStateMatch;
    });

    if (selectAll) {
      // Deselect all UIDs
      setSelectedUids(new Set());
    } else {
      // Select only filtered UIDs
      const filteredUidIds = filteredUids.map(uid => uid.uid);
      setSelectedUids(new Set(filteredUidIds));
    }
    setSelectAll(!selectAll);
  };

  // Check if a UID is selected
  const isUidSelected = (uidId) => {
    return selectedUids.has(uidId);
  };

  // Handle download selected UIDs
  const handleDownloadSelected = () => {
    if (selectedUids.size === 0) {
      showErrorPopup("Please select at least one UID to download");
      return;
    }

    const selectedUidData = uidData.filter(uid => selectedUids.has(uid.uid));

    // Create CSV content with proper formatting
    const headers = ['S.No.', 'UID', 'VMID', 'VM Name', 'Last Update', 'Status', 'Mapping State'];
    
    // Create CSV rows with proper escaping
    const csvRows = selectedUidData.map((uid, index) => {
      const row = [
        index + 1,
        uid.uid || 'N/A',
        uid.vmId || 'N/A',
        uid.vmName || 'N/A',
        formatDate(uid.lastUpdatedAt),
        uid.status || 'N/A', // Use raw status directly
        uid.mappingState || 'N/A'
      ];
      
      // Escape fields that might contain commas or quotes
      return row.map(field => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      }).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];

    link.href = url;
    link.setAttribute('download', `selected_uids_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle view details with conditional routing based on mapping state
  const handleViewDetails = (uid) => {
    if (uid.mappingState === 'Mapped') {
      navigate(`/home/configuration/uid-unmap/${uid.uid}`);
    } else if (uid.mappingState === 'Unmapped') {
      navigate(`/home/configuration/uid-map/${uid.uid}`);
    } else {
      navigate(`/home/configuration/vmid-details/${uid.uid}`);
    }
  };

  // Handle bulk upload
  const handleBulkUpload = () => {
    // Convert selected UIDs to array and pass to bulk upload page
    const selectedUidsArray = Array.from(selectedUids);
    navigate('/home/configuration/bulk-upload', { 
      state: { selectedUids: selectedUidsArray } 
    });
  };

  // Filter handlers
  const handleStatusFilterClick = (event) => {
    if (event) {
      setStatusFilterAnchorEl(event.currentTarget);
    } else {
      setStatusFilterAnchorEl(null);
    }
  };

  const handleMappingFilterClick = (event) => {
    if (event) {
      setMappingFilterAnchorEl(event.currentTarget);
    } else {
      setMappingFilterAnchorEl(null);
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    handleStatusFilterClick(null);
  };

  const handleMappingStateFilterChange = (value) => {
    setMappingStateFilter(value);
    handleMappingFilterClick(null);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setMappingStateFilter('all');
  };

  // Get unique status and mapping state values for filter options
  const statusOptions = [...new Set(uidData.map(uid => uid.status?.toString()).filter(Boolean))];
  const mappingStateOptions = [...new Set(uidData.map(uid => uid.mappingState).filter(Boolean))];

  // Create UID handlers
  const handleCreateUid = () => {
    setCreateUidOpen(true);
  };

  const handleCloseCreateUid = () => {
    setCreateUidOpen(false);
    setNewUidData({ uid: '' });
  };

  // Handle UID count input with integer validation
  const handleUidCountChange = (value) => {
    // Remove any non-digit characters
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Remove leading zeros
    const finalValue = cleanValue.replace(/^0+/, '') || '';
    
    setNewUidData(prev => ({
      ...prev,
      uid: finalValue
    }));
  };

  const handleCreateUidSubmit = () => {
    if (!newUidData.uid.trim()) {
      showErrorPopup("Please enter the number of UIDs to create");
      return;
    }

    const count = parseInt(newUidData.uid);
    if (isNaN(count) || count <= 0) {
      showErrorPopup("Please enter a valid number (minimum 1)");
      return;
    }

    if (count > 100) {
      showErrorPopup("Maximum 100 UIDs can be created at once");
      return;
    }

    setApiLoading(true);
    onCreateUid(newUidData, (success) => {
      setApiLoading(false);
      if (success) {
        handleCloseCreateUid();
      }
    });
  };

  // Input change handlers
  const handleInputChange = (field, value) => {
    if (field === 'uid') {
      handleUidCountChange(value);
    } else {
      setNewUidData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Format date using Moment.js (UTC to IST)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Convert UTC to IST and format
      return moment.utc(dateString).tz('Asia/Kolkata').format('DD/MM/YYYY, hh:mm:ss A');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // List page handlers
  const handleBackFromList = () => {
    setShowListPage(false);
    setPdfDownloading(false);
  };

  // Handle download from success dialog
  const handleDownloadAndRedirect = () => {
    if (createdUids.length === 0) {
      showErrorPopup("No UIDs available for download");
      return;
    }

    console.log('Starting download process for', createdUids.length, 'UIDs');
    
    // First download CSV
    downloadCreatedUidsCSV();
    
    // Then show the list page
    setShowListPage(true);
    setShowSuccessDialog(false);
    
    // Set the UIDs for QR generation and trigger the process
    setQrUids(createdUids);
    
    // Start PDF download after a short delay
    setTimeout(() => {
      console.log('Starting PDF download process');
      setPdfDownloading(true);
      setShowBulkQRDialog(true);
    }, 1500);
  };

  // Download created UIDs as CSV
  const downloadCreatedUidsCSV = () => {
    const headers = ['S.No.', 'UID', 'Status', 'Admin Name', 'Mapping State', 'Created At'];
    
    const csvRows = createdUids.map((uid, index) => {
      const row = [
        index + 1,
        uid.uid || 'N/A',
        uid.status || 'N/A', // Use raw status directly
        uid.createdByName || 'N/A',
        uid.mappingState || 'N/A',
        formatDate(uid.createdAt)
      ];
      
      return row.map(field => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      }).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];

    link.href = url;
    link.setAttribute('download', `created_uids_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle PDF download from list page
  const handleStartPDFDownload = () => {
    if (createdUids.length === 0) {
      showErrorPopup("No UIDs available for download");
      return;
    }
    console.log('Manual PDF download triggered');
    setPdfDownloading(true);
    setQrUids(createdUids);
    setShowBulkQRDialog(true);
  };

  // Handle PDF download completion properly
  const handlePDFDownloadComplete = () => {
    console.log('PDF download completed');
    setPdfDownloading(false);
    setShowBulkQRDialog(false);
    setQrUids([]);
  };

  // Handle Bulk QR Dialog close properly
  const handleBulkQRDialogClose = () => {
    console.log('Bulk QR dialog closed');
    setShowBulkQRDialog(false);
    setPdfDownloading(false);
    setQrUids([]);
  };

  // Create new UIDs
  const onCreateUid = (uidData, callback) => {
    const count = parseInt(uidData.uid);
    
    if (!count || count <= 0) {
      showErrorPopup("Please enter a valid count (minimum 1)");
      callback(false);
      return;
    }
    
    if (count > 100) {
      showErrorPopup("Maximum 100 UIDs can be created at once");
      callback(false);
      return;
    }

    const payload = {
      count: parseInt(count)
    };

    console.log('Creating UIDs with count:', count);

    props.getDataFromAPI(
      "/partner/api/v2/uid/create",
      "POST",
      payload,
      (response) => {
        console.log("Create UID Response:", response);
        
        let createdUidsData = [];
        
        // Handle different response formats
        if (Array.isArray(response)) {
          createdUidsData = response;
        } else if (response && response.status && response.status.success === true) {
          if (Array.isArray(response.uids)) {
            createdUidsData = response.uids;
          } else if (response.data && Array.isArray(response.data)) {
            createdUidsData = response.data;
          }
        } else if (response && typeof response === 'object') {
          // Try to extract UIDs from object
          createdUidsData = [response];
        }
        
        console.log("Extracted UIDs Data:", createdUidsData);
        
        if (createdUidsData.length > 0) {
          setCreatedUids(createdUidsData);
          setShowSuccessDialog(true);
          callback(true);
          loadUids(0, searchTerm); // Refresh the table
        } else {
          showErrorPopup("UIDs were created but no data returned");
          callback(false);
        }
      },
      (error) => {
        const errorMsg = error.message || "Failed to create UIDs";
        console.log("API Error:", errorMsg);
        showErrorPopup(errorMsg);
        callback(false);
      }
    );
  };

  // Show error popup
  const showErrorPopup = (message) => {
    setErrorMessage(message);
    setShowErrorDialog(true);
  };

  // Close error dialog
  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false);
    setErrorMessage('');
  };

  // Close success dialog
  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    setCreatedUids([]);
  };

  return (
    <VmIdDashboardComponent
      uids={uidData}
      loading={loading}
      error={error}
      searchTerm={searchTerm}
      currentPage={currentPage}
      hasMore={hasMore}
      totalItems={totalCount}
      
      showSuccessDialog={showSuccessDialog}
      createdUids={createdUids}
      showErrorDialog={showErrorDialog}
      errorMessage={errorMessage}
      createUidOpen={createUidOpen}
      apiLoading={apiLoading}
      showListPage={showListPage}
      
      showBulkQRDialog={showBulkQRDialog}
      qrUids={qrUids}
      pdfDownloading={pdfDownloading}
      setShowBulkQRDialog={setShowBulkQRDialog}
      
      statusFilter={statusFilter}
      mappingStateFilter={mappingStateFilter}
      statusFilterAnchorEl={statusFilterAnchorEl}
      mappingFilterAnchorEl={mappingFilterAnchorEl}
      statusOptions={statusOptions}
      mappingStateOptions={mappingStateOptions}
      
      selectedUids={selectedUids}
      selectAll={selectAll}
      
      newUidData={newUidData}
      
      navigate={navigate}
      
      setSearchTerm={handleSearch}
      onLoadNextPage={loadNextPage}
      handleCreateUid={handleCreateUid}
      handleCloseCreateUid={handleCloseCreateUid}
      handleCreateUidSubmit={handleCreateUidSubmit}
      handleBulkUpload={handleBulkUpload}
      handleInputChange={handleInputChange}
      handleUidSelect={handleUidSelect}
      handleSelectAll={handleSelectAll}
      isUidSelected={isUidSelected}
      handleDownloadSelected={handleDownloadSelected}
      handleViewDetails={handleViewDetails}
      handleStatusFilterClick={handleStatusFilterClick}
      handleMappingFilterClick={handleMappingFilterClick}
      handleStatusFilterChange={handleStatusFilterChange}
      handleMappingStateFilterChange={handleMappingStateFilterChange}
      handleClearFilters={handleClearFilters}
      handleBackFromList={handleBackFromList}
      onDownloadAndRedirect={handleDownloadAndRedirect}
      onStartPDFDownload={handleStartPDFDownload}
      onCloseSuccessDialog={handleCloseSuccessDialog}
      onCloseErrorDialog={handleCloseErrorDialog}
      onPDFDownloadComplete={handlePDFDownloadComplete}
      onBulkQRDialogClose={handleBulkQRDialogClose}
      formatDate={formatDate}
    />
  );
};

function mapStateToProps(state) {
  return {};
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(VmIdDashboardContainer);